import { FC, useMemo } from "react";

import { HStack, Text, VStack } from "@/components/ui";

import { formatPercent } from "./colors";
import { FieldLabel, Segmented } from "./Controls";
import { STATES } from "./geo";
import {
  DEFAULT_SETTINGS,
  GOVERNOR_INCUMBENCY,
  GOVERNOR_PULL,
  INDEPENDENT_KEEP,
  NON_VOTER_SHARE,
  OWN_WEIGHTS,
} from "./model";
import Sheet from "./Sheet";
import { briefingText, stateQuestions } from "./survey";
import type { ModelSettings, Race, TrackerVariant } from "./types";

interface MethodSheetProps {
  /** Current national turnout edge, shown in the calibration. */
  edge: number;
  onChange: (settings: ModelSettings) => void;
  onClose: () => void;
  open: boolean;
  races: Race[];
  settings: ModelSettings;
  /** State whose questionnaire is shown (defaults to Georgia). */
  state: string | null;
  /** The client view tunes the own-vote weight only and shows the bare
      questions. */
  variant: TrackerVariant;
}

const OWN_WEIGHT_HINT =
  "Every respondent gives two reads on each race: who they would vote for (own vote) and how they expect the people around them to vote (neighbors). This sets how much each counts in the forecast.";
const OWN_WEIGHT_OPTIONS = OWN_WEIGHTS.map((value) => ({
  label: `${value * 100}%`,
  value,
}));
const MODEL_ERRORS = [3, 4, 5, 6, 8].map((value) => ({
  label: `${value} pts`,
  value,
}));
const EXTERNAL_WEIGHTS = [0.25, 0.5, 0.75].map((value) => ({
  label: `${value * 100}%`,
  value,
}));

const Paragraph: FC<{ text: string }> = ({ text }) => (
  <Text className="text-sm leading-5 text-typography-700">{text}</Text>
);

/** How the tracker reads a race, its tunable settings and the questionnaire. */
const MethodSheet: FC<MethodSheetProps> = ({
  edge,
  onChange,
  onClose,
  open,
  races,
  settings,
  state,
  variant,
}) => {
  const admin = variant === "admin";
  const preview = STATES.find((s) => s.abbr === (state ?? "GA")) ?? STATES[0];
  const briefing = useMemo(() => briefingText(preview, new Date()), [preview]);
  const questions = useMemo(
    () =>
      stateQuestions(preview, races, {
        includeContext: false,
        includeWhy: false,
        offices: ["senate", "governor", "house"],
      }),
    [preview, races]
  );

  const settingsSection = (
    <VStack className="gap-4">
      <FieldLabel label="Settings" />
      <VStack className="gap-1.5">
        <Text className="text-sm font-medium text-typography-800">
          Weight of own vote vs neighbors
        </Text>
        {!admin && (
          <Text className="text-xs leading-4 text-typography-500">
            {OWN_WEIGHT_HINT}
          </Text>
        )}
        <Segmented
          onChange={(ownWeight) => onChange({ ...settings, ownWeight })}
          options={OWN_WEIGHT_OPTIONS}
          value={settings.ownWeight}
        />
      </VStack>
      {admin && (
        <>
          <VStack className="gap-1.5">
            <Text className="text-sm font-medium text-typography-800">
              Model error added to every race (margin, 1 s.d.)
            </Text>
            <Segmented
              onChange={(modelError) => onChange({ ...settings, modelError })}
              options={MODEL_ERRORS}
              value={settings.modelError}
            />
          </VStack>
          <VStack className="gap-1.5">
            <Text className="text-sm font-medium text-typography-800">
              Weight of the external source in the ensemble layer
            </Text>
            <Segmented
              onChange={(externalWeight) =>
                onChange({ ...settings, externalWeight })
              }
              options={EXTERNAL_WEIGHTS}
              value={settings.externalWeight}
            />
          </VStack>
        </>
      )}
      <Text className="text-xs text-typography-400">
        {admin
          ? `Defaults: ${DEFAULT_SETTINGS.ownWeight * 100}% own vote, ${DEFAULT_SETTINGS.modelError} pts model error, ${DEFAULT_SETTINGS.externalWeight * 100}% external. Saved in this browser.`
          : `Default: ${DEFAULT_SETTINGS.ownWeight * 100}% own vote. Saved in this browser.`}
      </Text>
    </VStack>
  );

  return (
    <Sheet
      onClose={onClose}
      open={open}
      subtitle="Synthetic likely-voter surveys, three reads per race"
      title="Method"
    >
      {!admin && settingsSection}

      <VStack className="gap-3">
        <FieldLabel label="How a state is surveyed" />
        <Paragraph
          text={`Each state gets its own study: a random draw of adults (18+) from Foresight's census-grounded population of that state, so counties, ages and sexes come in their real proportions. ${admin ? "Each respondent's race, education and urban/suburban/rural setting are then drawn from their own county's census figures (ACS 2020–2024; 2020 Census for urban/rural), not national averages." : "Each respondent's race, education, urban/suburban/rural setting, and opinions are drawn from Foresight's digital twin population and reflect their county's population rather than national averages."} Every respondent answers the same questionnaire about the races on their ballot. Only seats on the 2026 ballot are asked about.`}
        />
        <Paragraph
          text={`Each respondent also knows how they voted in 2024, or that they didn't (those under 20 were too young to): drawn from a turnout and vote model fitted to their county's certified result and adjusted for their race, education, age and sex, so every county's 2024 vote is reproduced on average. They also know whether they voted in the 2022 midterms (those under 22 were too young to): about two in three 2024 voters did, fewer among the young and those without degrees (Census CPS), one in ten 2024 non-voters did (Pew validated voters), and each state's 2022 turnout is reproduced on average.${admin ? " Midterm habits are what most separate 2026 voters from 2024 ones." : ""}`}
        />
        {admin && (
          <Paragraph text="Before the first question, the interviewer reads each respondent a short briefing: the date, how voting works in their state, who is on the ballot and the main news since June 2026, where the model's knowledge ends (shown with the questionnaire below). It is written to be neutral and carries no polls, forecasts or ratings, so their 2026 answers are their own reasoning from there. Each respondent gives a single answer per question." />
        )}
        <FieldLabel label="Three reads per race (two for the House)" />
        <Paragraph
          text={`1 · Own vote: 'who would you vote for?'${admin ? ", weighted by stated likelihood to vote (already voted 100%, definitely 90%, probably 65%, 50-50 35%, probably not 10%)" : ""}.`}
        />
        <Paragraph text="2 · Neighbors: 'how will the people around you vote?'. Social-circle questions called more swing states than own-intention questions in the 2016 US and 2017 French elections (Galesic et al., Nature Human Behaviour, 2018). The 'neighbor' polls YouGov fielded in 2024 for the French trader who bet on Trump pointed the right way in MI, NC, PA and WI but overshot Trump, and YouGov found them less accurate than the standard question, so this read is blended, never used alone. Its value here: it sidesteps shy-voter effects and the respondent's own lean." />
        <Paragraph
          text={`3 · Expectations: 'who will win?'. Expectations called 81% of state races vs 69% for intentions in ANES data, 1952–2008 (Rothschild & Wolfers). Shown for reference, not blended, and asked for Senate and governor races only: the House generic ballot has no named candidates to call.${admin ? " Like Trump approval, top issue and household finances, it is asked only in waves fielded with the context questions; by default a wave asks just what feeds the forecast." : ""}`}
        />
        <FieldLabel label="Calibration against 2024" />
        <Paragraph
          text={`Respondents also report their 2024 vote and how they think their neighbors voted in 2024. We reweight Trump and Harris voters so the sample's recalled vote matches the certified state result (recalled-vote weighting, as several major pollsters now do), and subtract the gap between perceived and actual 2024 neighborhood votes from the neighbor read. This corrects a sample that leans one way${admin ? ", a known risk with LLM-simulated respondents (off-the-shelf models overstated Harris in 2024)" : ""}. One caveat from 2026 polling: some real 2024 Trump voters no longer report it, so weighting to his 2024 share can slightly favor Republicans (about 1 point on the generic ballot).`}
        />
        <Paragraph
          text={`Turnout is calibrated too. Respondents who didn't vote in 2024 (or were too young to) make up about a fifth of a state's likely voters in the simulation and lean heavily Democratic, while validated-voter data puts people who skipped the previous presidential election at roughly 3–8% of a midterm electorate (Pew: 68% of 2020 voters turned out again in 2022, as turnout fell from 66% to 46%). We cap them at ${formatPercent(NON_VOTER_SHARE)} of each state's likely voters. One national turnout edge then weighs Harris voters ×${edge.toFixed(2)} and Trump voters ×${(1 / edge).toFixed(2)} in every state, set so the survey keeps its own national House vote: the cap changes how that vote spreads across states, not its level.`}
        />
        <Paragraph text="Counties are read from the respondents living there and, when they are few, pulled toward the county's 2024 result moved by the state's swing (8 pseudo-respondents of shrinkage), so a county with 3 respondents keeps its usual lean instead of swinging wildly." />
        <FieldLabel label="Adjustments" />
        <Paragraph
          text={`Governors running for re-election get ${GOVERNOR_INCUMBENCY} points on the margin: governor races are more candidate-driven than federal ones, and the simulated respondents barely reward incumbents. Senate and House races get no bonus.`}
        />
        <Paragraph
          text={`Where an independent's simulated support runs far beyond what their state's partisanship suggests (this year, Osborn in Nebraska), they keep ${formatPercent(INDEPENDENT_KEEP)} of their own-vote lead over the state's 2024 presidential result (Harris's margin, for an independent on the Democratic side), since independents usually fade by Election Day. Other independents are read as respondents answer, and the neighbor read is left as is.`}
        />
        <Paragraph
          text={`Governor races then keep ${formatPercent(GOVERNOR_PULL)} of their distance from the national environment (the generic ballot read): the simulated respondents split their tickets less than real voters, and governor races, the most candidate-driven, are where real voters split them most. Senate and House reads are left as they are.`}
        />
        <FieldLabel label="House seats" />
        <Paragraph text="The survey asks the generic ballot (Democrat or Republican for Congress). Each of the 435 districts, as drawn for 2026 (new maps in nine states), starts from its 2024 presidential result within those lines (The Downballot's county-by-county calculations) and moves by its state's swing. A district adds its own deviation only as far as its counties have respondents: it needs about 400 before its own read weighs as much as the state's, so at 1,000 respondents per state the state swing dominates. States not surveyed yet take the surveyed states' average swing, with extra error. Each district also carries 5 points of error for local trends, which its own respondents can narrow, and 3.5 points for candidates and incumbency, which a generic ballot can't see. Seats whose finalists share a party, or with a single candidate, are decided; an independent standing in for a missing major-party candidate (Alaska's Hill, California's Kiley) is read on that side." />
        <Paragraph text="Seat odds come from 5,000 simulated elections with errors shared nationally (3 pts), within each state (its survey error) and per district. 218 seats for control." />
        {admin && (
          <>
            <FieldLabel label="Published forecasts" />
            <Paragraph text="Silver Bulletin's FLIPR forecast (Deluxe, Oct 28, 10:57 ET) is built in for comparison: every Senate, governor and House race, plus its own control odds. The public polls' race averages are built in too. Other forecasters can be pasted in Sources. External numbers are never shown to the simulated respondents." />
          </>
        )}
        <FieldLabel label="Early vote" />
        <Paragraph text="Each state's panel shows the ballots already cast, by state and (where reported) county, with the party registration of those voters where states publish it, from Call the Map (callthemap.com/early-vote). It is context only and never moves the forecast: registration is not a vote, there is no same-day 2022 or 2024 comparison, and Republicans' recent shift to early voting would read as enthusiasm." />
      </VStack>

      {admin && settingsSection}

      <VStack className="gap-3">
        <FieldLabel
          label={
            admin
              ? `Questionnaire · ${preview.name}`
              : `Questionnaire (example for ${preview.name})`
          }
        />
        {admin && (
          <Text className="text-xs leading-4 text-typography-500">
            {briefing}
          </Text>
        )}
        {questions.map((q, i) => (
          <VStack className="gap-1" key={i}>
            <HStack className="gap-2">
              <Text className="w-6 text-sm font-semibold text-typography-400">
                {i + 1}
              </Text>
              <Text className="flex-1 text-sm text-typography-900">
                {q.text}
              </Text>
            </HStack>
            {admin && q.options.length > 0 && (
              <Text className="pl-8 text-xs leading-4 text-typography-500">
                {q.options.join(" · ")}
              </Text>
            )}
          </VStack>
        ))}
      </VStack>
    </Sheet>
  );
};

export default MethodSheet;
